/**
 * Non-blocking moderation progress toasts (bottom-right, stack upward).
 *
 * A single provider owns a stack of toasts. Pages call `useModerationRunner().run(...)`
 * with a list of comments and a per-item runner; the provider drives them one at a
 * time (concurrency 1, ~200ms spacing), updating each row from the REAL API result —
 * never a timer. It polls the page's Meta rate-limit usage between items to back off
 * at ≥80% and hard-stops when Facebook throttles (HTTP 429 / usage 100%).
 *
 * The user keeps working while it runs. Closing a toast hides it but the run
 * continues. Multiple actions stack.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Loader2,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
  RotateCcw,
} from "lucide-react";
import { useApi, API_ENDPOINTS } from "../lib/api";

// App palette (per handoff spec)
const C = {
  primary: "#2563eb",
  danger: "#dc2626",
  success: "#16a34a",
  warning: "#d97706",
};

export interface RunItemResult {
  success: boolean;
  error?: string;
  throttled?: boolean;
  retryMinutes?: number;
}

export interface RunConfig {
  pageId: string;
  action: string; // delete | hide | approve | reply | dm | delete-and-block | confirm-hide
  items: { id: string; handle: string }[];
  runItem: (id: string) => Promise<RunItemResult>;
}

type RowStatus = "pending" | "done" | "failed";
interface Row {
  id: string;
  handle: string;
  status: RowStatus;
  error?: string;
}
type Phase = "running" | "clean" | "errors" | "throttled";
interface Entry {
  key: string;
  action: string;
  total: number;
  rows: Row[];
  phase: Phase;
  expanded: boolean;
  hidden: boolean;
  note?: string;
  retryMinutes?: number;
  config: RunConfig;
}

const GERUND: Record<string, string> = {
  delete: "Deleting",
  hide: "Hiding",
  approve: "Approving",
  reply: "Replying to",
  dm: "Messaging",
  "delete-and-block": "Deleting & blocking",
  "confirm-hide": "Confirming",
};
const PAST: Record<string, string> = {
  delete: "deleted",
  hide: "hidden",
  approve: "approved",
  reply: "replied to",
  dm: "messaged",
  "delete-and-block": "deleted & blocked",
  "confirm-hide": "confirmed",
};
const gerundOf = (a: string) => GERUND[a] || "Processing";
const pastOf = (a: string) => PAST[a] || `${a}ed`;
const nounOf = (n: number) => (n === 1 ? "comment" : "comments");
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface Ctx {
  run: (config: RunConfig) => void;
}
const ModerationToastCtx = createContext<Ctx | null>(null);

export const useModerationRunner = (): Ctx => {
  const ctx = useContext(ModerationToastCtx);
  if (!ctx)
    throw new Error("useModerationRunner must be used within ModerationToastProvider");
  return ctx;
};

export const ModerationToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const api = useApi();
  const [entries, setEntries] = useState<Entry[]>([]);
  const keySeq = useRef(0);

  const patch = useCallback(
    (key: string, fn: (e: Entry) => Entry) =>
      setEntries((prev) => prev.map((e) => (e.key === key ? fn(e) : e))),
    []
  );
  const remove = useCallback(
    (key: string) => setEntries((prev) => prev.filter((e) => e.key !== key)),
    []
  );

  const counts = (rows: Row[]) => ({
    done: rows.filter((r) => r.status === "done").length,
    failed: rows.filter((r) => r.status === "failed").length,
  });

  // Drive a subset of an entry's rows (all rows on first run, only failed on retry).
  const drive = useCallback(
    async (key: string, config: RunConfig, idsToRun: string[]) => {
      for (let i = 0; i < idsToRun.length; i++) {
        const id = idsToRun[i];

        // Back off / stop based on real Meta usage (polled, not every single item).
        if (i % 4 === 0) {
          try {
            const u = (await api.get(API_ENDPOINTS.RATE_LIMIT(config.pageId)))
              .data as { percent: number; throttled: boolean; retryMinutes: number };
            if (u?.throttled) {
              abort(key, u.retryMinutes);
              return;
            }
            if (u && u.percent >= 80) {
              patch(key, (e) => ({
                ...e,
                note: "Slowing down to respect Facebook limits…",
              }));
              await sleep(4000);
              patch(key, (e) => ({ ...e, note: undefined }));
            }
          } catch {
            /* usage poll is best-effort */
          }
        }

        let result: RunItemResult;
        try {
          result = await config.runItem(id);
        } catch (err: any) {
          const st = err?.response?.status;
          result = {
            success: false,
            error: err?.response?.data?.error || err?.message || "Failed",
            throttled: st === 429 || !!err?.response?.data?.throttled,
            retryMinutes: err?.response?.data?.retryMinutes,
          };
        }

        patch(key, (e) => {
          const rows = e.rows.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: (result.success ? "done" : "failed") as RowStatus,
                  error: result.error,
                }
              : r
          );
          return { ...e, rows };
        });

        if (result.throttled) {
          abort(key, result.retryMinutes);
          return;
        }
        await sleep(200);
      }
      finalize(key);
    },
    [api, patch]
  );

  // Mark all still-pending rows as failed (rate limited) and freeze the toast open.
  const abort = useCallback(
    (key: string, retryMinutes?: number) => {
      patch(key, (e) => ({
        ...e,
        rows: e.rows.map((r) =>
          r.status === "pending"
            ? { ...r, status: "failed" as RowStatus, error: "Rate limited by Facebook" }
            : r
        ),
        phase: "throttled",
        note: undefined,
        retryMinutes,
      }));
    },
    [patch]
  );

  const finalize = useCallback(
    (key: string) => {
      setEntries((prev) => {
        const e = prev.find((x) => x.key === key);
        if (!e) return prev;
        const { failed } = counts(e.rows);
        if (failed === 0) {
          // Clean: auto-dismiss after 5s (skip if the user already closed it).
          setTimeout(() => remove(key), 5000);
          return prev.map((x) =>
            x.key === key ? { ...x, phase: "clean", note: undefined } : x
          );
        }
        return prev.map((x) =>
          x.key === key ? { ...x, phase: "errors", note: undefined } : x
        );
      });
    },
    [remove]
  );

  const run = useCallback(
    (config: RunConfig) => {
      if (!config.items.length) return;
      const key = `mt-${++keySeq.current}`;
      const entry: Entry = {
        key,
        action: config.action,
        total: config.items.length,
        rows: config.items.map((it) => ({
          id: it.id,
          handle: it.handle || "commenter",
          status: "pending",
        })),
        phase: "running",
        expanded: false,
        hidden: false,
        config,
      };
      setEntries((prev) => [...prev, entry]);
      drive(key, config, config.items.map((it) => it.id));
    },
    [drive]
  );

  const retryFailed = useCallback(
    (key: string) => {
      setEntries((prev) => {
        const e = prev.find((x) => x.key === key);
        if (!e) return prev;
        const failedIds = e.rows.filter((r) => r.status === "failed").map((r) => r.id);
        if (!failedIds.length) return prev;
        // Reset failed rows to pending, re-run just those.
        const next = prev.map((x) =>
          x.key === key
            ? {
                ...x,
                phase: "running" as Phase,
                retryMinutes: undefined,
                rows: x.rows.map((r) =>
                  r.status === "failed"
                    ? { ...r, status: "pending" as RowStatus, error: undefined }
                    : r
                ),
              }
            : x
        );
        setTimeout(() => drive(key, e.config, failedIds), 0);
        return next;
      });
    },
    [drive]
  );

  const ctx: Ctx = { run };

  return (
    <ModerationToastCtx.Provider value={ctx}>
      {children}
      <div className="pointer-events-none fixed top-4 right-4 z-[120] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-3">
        {entries
          .filter((e) => !e.hidden)
          .map((e) => (
            <ToastCard
              key={e.key}
              entry={e}
              onClose={() => patch(e.key, (x) => ({ ...x, hidden: true }))}
              onToggle={() =>
                patch(e.key, (x) => ({ ...x, expanded: !x.expanded }))
              }
              onRetry={() => retryFailed(e.key)}
            />
          ))}
      </div>
    </ModerationToastCtx.Provider>
  );
};

const dotColor = (s: RowStatus) =>
  s === "done" ? C.success : s === "failed" ? C.danger : "#9ca3af";

const ToastCard: React.FC<{
  entry: Entry;
  onClose: () => void;
  onToggle: () => void;
  onRetry: () => void;
}> = ({ entry, onClose, onToggle, onRetry }) => {
  const done = entry.rows.filter((r) => r.status === "done").length;
  const failed = entry.rows.filter((r) => r.status === "failed").length;
  const processed = done + failed;
  const pct = Math.round((processed / Math.max(1, entry.total)) * 100);
  const running = entry.phase === "running";

  let icon: React.ReactNode;
  let title: string;
  if (running) {
    icon = <Loader2 className="h-5 w-5 animate-spin" style={{ color: C.primary }} />;
    title = `${gerundOf(entry.action)} ${entry.total} ${nounOf(entry.total)}`;
  } else if (entry.phase === "clean") {
    icon = <Check className="h-5 w-5" style={{ color: C.success }} />;
    title = `${entry.total} ${nounOf(entry.total)} ${pastOf(entry.action)}`;
  } else if (entry.phase === "throttled") {
    icon = <AlertTriangle className="h-5 w-5" style={{ color: C.warning }} />;
    title = `Paused by Facebook — ${done} ${pastOf(entry.action)}`;
  } else {
    icon = <AlertTriangle className="h-5 w-5" style={{ color: C.warning }} />;
    title = `${done} ${pastOf(entry.action)}, ${failed} failed`;
  }

  return (
    <div
      className="pointer-events-auto overflow-hidden rounded-xl bg-white"
      style={{
        boxShadow:
          "0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)",
        fontFamily:
          "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="mt-0.5 shrink-0">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900">
            {title}
          </div>
          {running && (
            <div className="mt-0.5 text-xs text-gray-500">
              {processed} of {entry.total}
            </div>
          )}
          {entry.note && (
            <div className="mt-0.5 text-xs" style={{ color: C.warning }}>
              {entry.note}
            </div>
          )}
          {entry.phase === "throttled" && (
            <div className="mt-0.5 text-xs" style={{ color: C.warning }}>
              {entry.retryMinutes
                ? `Retry in about ${entry.retryMinutes} min.`
                : "Please wait a moment."}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={onToggle}
            aria-label={entry.expanded ? "Collapse" : "Expand"}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            {entry.expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onClose}
            aria-label="Dismiss"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {running && (
        <div className="h-1 w-full bg-gray-100">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${pct}%`, backgroundColor: C.primary }}
          />
        </div>
      )}

      {/* Retry (errors / throttled) */}
      {(entry.phase === "errors" || entry.phase === "throttled") && failed > 0 && (
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-3.5 py-2">
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: C.primary }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Retry failed ({failed})
          </button>
        </div>
      )}

      {/* Per-item list */}
      {entry.expanded && (
        <div className="max-h-52 overflow-y-auto border-t border-gray-100">
          {entry.rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-2 px-3.5 py-1.5 text-xs"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: dotColor(r.status) }}
              />
              <span className="min-w-0 flex-1 truncate text-gray-700">
                {r.handle}
              </span>
              <span
                className="shrink-0 font-medium capitalize"
                style={{
                  color:
                    r.status === "done"
                      ? C.success
                      : r.status === "failed"
                      ? C.danger
                      : "#6b7280",
                }}
              >
                {r.status === "done"
                  ? "Done"
                  : r.status === "failed"
                  ? "Failed"
                  : "Pending"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationToastProvider;
